import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { cx } from "../utils/cx";
import { useFormField } from "./FormField";

/**
 * Inputs pick up id / aria-describedby / aria-invalid from an enclosing
 * <FormField>; explicit props always win. All ≥44px tall (§14.3).
 */

function useFieldWiring(props: {
  id?: string | undefined;
  invalid?: boolean | undefined;
  describedBy?: string | undefined;
}) {
  const field = useFormField();
  return {
    id: props.id ?? field?.controlId,
    invalid: props.invalid ?? field?.invalid ?? false,
    describedBy: props.describedBy ?? field?.describedBy,
  };
}

/* ------------------------------------------------------------------ */

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly invalid?: boolean;
  /** RTL-safe adornments: rendered on logical start/end. */
  readonly adornStart?: ReactNode;
  readonly adornEnd?: ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { invalid, adornStart, adornEnd, className, id, "aria-describedby": describedBy, ...rest },
  ref,
) {
  const wiring = useFieldWiring({ id, invalid, describedBy });
  const input = (
    <input
      ref={ref}
      id={wiring.id}
      className={cx("ui-input", className)}
      aria-invalid={wiring.invalid || undefined}
      aria-describedby={wiring.describedBy}
      {...rest}
    />
  );
  const hasStart = adornStart != null;
  const hasEnd = adornEnd != null;
  if (!hasStart && !hasEnd) return input;
  return (
    <span className={cx("ui-control", hasStart && "ui-control--start", hasEnd && "ui-control--end")}>
      {hasStart ? <span className="ui-control-adorn ui-control-adorn--start">{adornStart}</span> : null}
      {input}
      {hasEnd ? <span className="ui-control-adorn ui-control-adorn--end">{adornEnd}</span> : null}
    </span>
  );
});

/* ------------------------------------------------------------------ */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, id, "aria-describedby": describedBy, ...rest },
  ref,
) {
  const wiring = useFieldWiring({ id, invalid, describedBy });
  return (
    <textarea
      ref={ref}
      id={wiring.id}
      className={cx("ui-textarea", className)}
      aria-invalid={wiring.invalid || undefined}
      aria-describedby={wiring.describedBy}
      {...rest}
    />
  );
});

/* ------------------------------------------------------------------ */

/**
 * NumberInput — amounts, quantities, fees (§14.5/§14.4 price breakdowns).
 * Tabular figures (tokens.json §numerals); `inputMode` keys the right mobile
 * keypad. Numeric wire values stay Western digits; DISPLAY formatting per
 * locale is the app's job via Intl.NumberFormat.
 */
export interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  readonly invalid?: boolean;
  /** "decimal" (default — money) or "numeric" (integers: counts, minutes). */
  readonly keypad?: "decimal" | "numeric";
  /** Unit adornment slot (e.g. currency code) rendered at the logical end. */
  readonly unit?: ReactNode;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { keypad = "decimal", unit, className, ...rest },
  ref,
) {
  return (
    <TextInput
      ref={ref}
      type="number"
      inputMode={keypad}
      dir="ltr"
      className={cx("ui-input--number ui-tnum", className)}
      adornEnd={unit}
      {...rest}
    />
  );
});

/**
 * DateInput — native picker (desktop-first admin, §14.3); the browser
 * renders the calendar in the user's locale. Values stay ISO (YYYY-MM-DD).
 */
export interface DateInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  readonly invalid?: boolean;
  /** "date" (default) or "datetime-local" for windows with a time component. */
  readonly withTime?: boolean;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { withTime = false, className, ...rest },
  ref,
) {
  return (
    <TextInput
      ref={ref}
      type={withTime ? "datetime-local" : "date"}
      className={cx("ui-input--date", className)}
      {...rest}
    />
  );
});

/**
 * PhoneInput — phone numbers are ALWAYS rendered LTR, even inside an RTL
 * layout (§14.2): digits + leading "+" would visually scramble under bidi.
 * Wire format is E.164; the app owns any pretty-printing.
 */
export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  readonly invalid?: boolean;
  /** Fixed dial-code adornment (e.g. "+964") when the country is known. */
  readonly dialCode?: string;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { dialCode, className, ...rest },
  ref,
) {
  return (
    <TextInput
      ref={ref}
      type="tel"
      inputMode="tel"
      dir="ltr"
      autoComplete="tel"
      className={cx("ui-input--phone ui-tnum", className)}
      adornStart={dialCode ? <span className="ui-input-dialcode ui-tnum" dir="ltr">{dialCode}</span> : undefined}
      {...rest}
    />
  );
});

/**
 * SearchInput — every list screen has search (§14.3). Standalone usage
 * (above a table, no <FormField>) REQUIRES `label` for screen readers.
 * The clear affordance renders only when `onClear` AND `clearLabel` are
 * both provided — an unlabeled icon button is never emitted.
 */
export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  readonly invalid?: boolean;
  /** Accessible name when used outside a <FormField> (i18n slot). */
  readonly label?: string;
  /** Clears the query (controlled value stays with the app). */
  readonly onClear?: () => void;
  /** Screen-reader label for the clear button (i18n slot). */
  readonly clearLabel?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { label, onClear, clearLabel, className, value, ...rest },
  ref,
) {
  const showClear = onClear != null && clearLabel != null && value != null && value !== "";
  return (
    <TextInput
      ref={ref}
      type="search"
      role="searchbox"
      aria-label={label}
      value={value}
      className={cx("ui-input--search", className)}
      adornStart={<Search size={16} aria-hidden="true" />}
      adornEnd={
        showClear ? (
          <button type="button" className="ui-input-clear" aria-label={clearLabel} onClick={onClear}>
            <X size={14} aria-hidden="true" />
          </button>
        ) : undefined
      }
      {...rest}
    />
  );
});

/* ------------------------------------------------------------------ */

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className, id, "aria-describedby": describedBy, children, ...rest },
  ref,
) {
  const wiring = useFieldWiring({ id, invalid, describedBy });
  return (
    <span className="ui-select-wrap">
      <select
        ref={ref}
        id={wiring.id}
        className={cx("ui-select", className)}
        aria-invalid={wiring.invalid || undefined}
        aria-describedby={wiring.describedBy}
        {...rest}
      >
        {children}
      </select>
      <span className="ui-select-chevron" aria-hidden="true">
        <ChevronDown size={16} />
      </span>
    </span>
  );
});
